'use strict'
import * as React from 'react'
import { SpinnerButton } from '../pages/common/CustomButton'

/** @type {ReturnType<typeof React.createContext<function(ModalProps):void>>} */
export const ModalContext = React.createContext(() => {})
ModalContext.displayName = 'ModalContext'

/** @type {ReturnType<typeof React.createContext<{_closeUpModal:function():void}>>} */
export const ModalExtraContext = React.createContext({})

export const useModal = () => {
  return React.useContext(ModalContext)
}
export const useModalExtra = () => {
  return React.useContext(ModalExtraContext)
}

/**
 * @typedef {Object} ModalProps
 * @property {string} title
 * @property {string} description
 * @property {React.ReactNode} body
 * @property {string} cancelName
 * @property {boolean} isCancel
 * @property {boolean} isWide
 * @property {boolean} isFooter
 * @property {boolean} isSpinnerDisabled
 * @property {{name:string, click: function():void, className:string, close:boolean}[]} button
 * @property {function():void} onClose
 * @property {string} footerClass
 * @property {string} footerButtonAreaClass - フッターのボタンエリア用クラス。末尾に付与して既定の gap-x-16 を上書きする（例: 'gap-x-8'）
 * @property {'left' | 'center'} titleAlign
 * @property {'icon' | 'text'} closeButtonStyle
 * @property {string} closeButtonText
 * @property {string} bodyClassName -
 */

export const ModalProvider = ({ children }) => {
  const [title, setTitle] = React.useState()
  const [description, setDescription] = React.useState()
  const [body, setBody] = React.useState()
  const [button, setButton] = React.useState([])
  const [cancelName, setCancelName] = React.useState()
  const [isCancel, setIsCancel] = React.useState()
  const [wide, setWide] = React.useState()
  const [isFooter, setIsFooter] = React.useState(true)
  const [isSpinnerDisabled, setIsSpinnerDisabled] = React.useState(true)
  const [footerClass, setFooterClass] = React.useState('')
  const [footerButtonAreaClass, setFooterButtonAreaClass] = React.useState(undefined)
  const [titleAlign, setTitleAlign] = React.useState('center')
  const [closeButtonStyle, setCloseButtonStyle] = React.useState('icon')
  const [closeButtonText, setCloseButtonText] = React.useState('閉じる')
  const [bodyClassName, setBodyClassName] = React.useState('')
  const [modalClass, setModalClass] = React.useState('')
  const [modalStyle, setModalStyle] = React.useState({})

  const onCloseRef = React.useRef(undefined)
  const closeUpModalRef = React.useRef(() => {})

  /**
   * @function showModal
   * @param {ModalProps} props
   */
  const showModal = ({
    title,
    description,
    body,
    button,
    cancelName = 'キャンセル',
    isCancel = true,
    isWide = true,
    isFooter = true,
    isSpinnerDisabled = false,
    footerClass = '',
    footerButtonAreaClass = undefined,
    modalClass: modalClassProp = '',
    modalStyle: modalStyleProp = {},
    onClose = undefined,
    titleAlign = 'center',
    closeButtonStyle = 'icon',
    closeButtonText = '閉じる',
    bodyClassName = undefined
  }) => {
    setTitle(title)
    setDescription(description)
    setBody(body)
    setModalClass(modalClassProp)
    setModalStyle(modalStyleProp || {})
    if (button) {
      setButton(button)
    } else {
      setButton([])
    }
    setCancelName(cancelName)
    setIsCancel(isCancel)
    setWide(isWide)
    setIsFooter(isFooter)
    setIsSpinnerDisabled(isSpinnerDisabled)
    setFooterClass(footerClass)
    setFooterButtonAreaClass(footerButtonAreaClass)
    setTitleAlign(titleAlign)
    setCloseButtonStyle(closeButtonStyle)
    setCloseButtonText(closeButtonText)
    setBodyClassName(bodyClassName ?? '')
    // モーダル表示
    _openUpModal(isWide)
    // モーダル表示（state 反映後に表示するため次のティックで実行）
    setTimeout(() => _openUpModal(isWide), 0)
    onCloseRef.current = onClose
  }

  const _openUpModal = (isWide) => {
    const modalBg = document.querySelector('.bg-modal-window')
    modalBg?.classList.add('is-active')
    const modalUploading = document.querySelector('#modal-context-modal-window--upload-confirm')
    modalUploading?.classList.remove('animate__fadeOut')
    modalUploading?.classList.add('animate__fadeIn')
    if (isWide) {
      modalUploading?.classList.add('modal-window__wide')
    } else {
      modalUploading?.classList.remove('modal-window__wide')
    }
    modalUploading?.classList.add('is-active')
  }

  const _closeUpModal = () => {
    const modalUploading = document.querySelector('#modal-context-modal-window--upload-confirm')
    if (!modalUploading?.classList.contains('animate__fadeOut')) {
      modalUploading?.classList.remove('animate__fadeIn')
      modalUploading?.classList.add('animate__fadeOut')
    }
    const fadeOutTimeoutSec = 300
    setTimeout(function () {
      document.querySelector('.bg-modal-window.is-active')?.classList.remove('is-active')
      document.querySelector('.modal-window.is-active')?.classList.remove('is-active')
      modalUploading?.classList.remove('modal-window__wide')
      setTitle(undefined)
      setDescription(undefined)
      setBody(undefined)
      setButton([])
      setBodyClassName('')
    }, fadeOutTimeoutSec)

    onCloseRef.current?.()
  }

  closeUpModalRef.current = _closeUpModal

  /**
   * フッターボタンを開いた後に更新する。フッターは showModal 時のスナップショットで固定される ため、本文側の state 変更（例: 種別選択）に応じてボタンの活性/非活性を反映するには、
   * 本文からこの関数を呼んでボタン配列を作り直す必要がある。
   *
   * @param {(prev: object[]) => object[] | object[]} updater 現在のボタン配列を受け取り新配列を返す関数、または新しいボタン配列
   */
  const updateButtons = React.useCallback((updater) => {
    setButton((prev) => (typeof updater === 'function' ? updater(prev) : updater))
  }, [])

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        const isActive = document.querySelector('.modal-window.is-active')
        if (isActive) {
          closeUpModalRef.current()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  React.useEffect(() => {
    const abortController = new AbortController()

    window.addEventListener(
      'popstate',
      () => {
        _closeUpModal()
      },
      { signal: abortController.signal }
    )

    return () => abortController.abort()
  }, [])

  // modalStyle が変更されたら DOM に直接適用（!important 付き）
  React.useEffect(() => {
    const modalElement = document.querySelector('#modal-context-modal-window--upload-confirm')
    if (modalElement) {
      // 既存の inline style をクリア（width など）
      modalElement.style.removeProperty('width')

      // modalStyle があれば適用
      if (modalStyle && Object.keys(modalStyle).length > 0) {
        Object.keys(modalStyle).forEach((key) => {
          modalElement.style.setProperty(key, modalStyle[key], 'important')
        })
      }
    }
  }, [modalStyle])

  return (
    <ModalContext.Provider value={showModal}>
      <ModalExtraContext.Provider value={{ _closeUpModal, updateButtons }}>
        {children}
        <div className="bg-modal-window w-screen h-screen fixed top-0 left-0 grey lighten-70 z-50"></div>
        <div
          id="modal-context-modal-window--upload-confirm"
          data-testid="modal_window"
          className={`modal-window fixed bg sub z-50 rounded-2xl animate__fadeIn ${modalClass}`.trim()}
          style={modalStyle}
        >
          <div className="modal-window__header">
            <div
              className={`modal-window__title flex items-center pt-10 ${
                titleAlign === 'left' ? 'justify-start pl-8' : 'justify-center'
              } ${titleAlign === 'left' ? 'border-b border-gray-300 pb-4' : ''}`}
            >
              <span
                className={`modal-window__title-text text-h3 font-bold ${
                  titleAlign === 'left' ? 'px-0' : 'px-10 text-center'
                }`}
              >
                {title}
              </span>
            </div>
            {description && (
              <div className="text-body text-caption-2 text-center grey--text text--lighten-70 pt-4 px-8 pb-6">
                {description}
              </div>
            )}
            {closeButtonStyle === 'text' ? (
              <button
                data-testid="modal_close"
                type="button"
                className="absolute top-5 right-5 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-base hover:bg-gray-600 hover:text-white transition-colors"
                onClick={() => _closeUpModal()}
              >
                {closeButtonText}
              </button>
            ) : (
              <button
                data-testid="modal_close"
                type="button"
                className="modal-window__btn-close absolute top-5 right-5"
                onClick={() => _closeUpModal()}
              >
                <span className="icon-close"></span>
              </button>
            )}
          </div>

          <div className="modal-window__content px-8 py-5">
            {body &&
              (wide ? (
                <div
                  className={`modal-window__upload-result box-xy-bordered rounded-xl px-10 pt-4 pb-14 mx-auto overflow-y-auto${bodyClassName ? ` ${bodyClassName}` : ''}`}
                >
                  {body}
                </div>
              ) : (
                <div>{body}</div>
              ))}
            {isFooter && (
              <div className={`modal-content-footer mt-8${footerClass ? ` ${footerClass}` : ''}`}>
                <div
                  className={`footer_button_area gap-x-16${footerButtonAreaClass ? ` ${footerButtonAreaClass}` : ''}`}
                >
                  {isCancel && (
                    <button
                      data-testid="modal_cancel"
                      className="footer-btn button_back"
                      onClick={() => _closeUpModal()}
                    >
                      {cancelName}{' '}
                    </button>
                  )}
                  {button.map((item, idx) => {
                    const { close = true, click } = item
                    return (
                      <SpinnerButton
                        {...item}
                        disableSpinner={isSpinnerDisabled}
                        key={idx}
                        name={item.name}
                        className={`${item.className}`}
                        onClick={async () => {
                          try {
                            await item.click()
                          } finally {
                            if (close) _closeUpModal()
                          }
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalExtraContext.Provider>
    </ModalContext.Provider>
  )
}
